import { ReactElement } from 'react';

import { BillingChannel, Permission, PlanFamily, SubscriptionStatus } from '@localstack/types';

import { Box, Button, Stack, Typography } from '@mui/material';

import { SubscriptionService, useApiGetter, useHasOnlySnowflakeAccess, useRoutes } from '@localstack/services';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { BasicPageLayout } from '~/layouts';

import { AppRoute } from '~/config';

import { STUDENT_PLAN_DESCRIPTION } from '../constants';

import { BillingDetails, Invoices, PaymentMethod, Subscriptions } from './components/Billing';
import { GridComponent, PageSection, SectionContainer, SectionTitle } from './components/Billing/utils';

const subtitle = (
  <span>
    For questions about billing, contact{' '}
    <b>
      <a href="mailto:support@localstack.cloud" style={{ color: 'inherit' }}>
        support@localstack.cloud
      </a>
    </b>
    .
  </span>
);

const StudentPlanPrompt = (): JSX.Element => {
  const { goto } = useRoutes();
  return (
    <SectionContainer>
      <GridComponent columns="1fr max-content">
        <Stack gap="4px">
          <Typography color="text.primary" fontWeight={500}>
            Student Plan
          </Typography>
          <Typography color="text.tertiary" sx={{ whiteSpace: 'pre-line' }}>
            {STUDENT_PLAN_DESCRIPTION}
          </Typography>

          <Button
            variant="outlined"
            sx={{ width: 'fit-content', marginTop: '20px' }}
            onClick={() => goto(AppRoute.STUDENT_VERIFICATION)}
          >
            Enable with GitHub Student Developer Pack
          </Button>
        </Stack>
        <Stack alignSelf="baseline" width="100%" alignContent="flex-end" flexWrap="wrap">
          <img alt="" src="/images/localstackstudents.svg" width="70%" />
        </Stack>
      </GridComponent>
    </SectionContainer>
  );
};

export const Billing = (): ReactElement => {
  const { can } = useAuthProvider();
  // TODO: in the future when we have subscription info in a state/quicker sub this to all trials here
  const userHasOnlySnowflakeSub = useHasOnlySnowflakeAccess({ useAuthProvider });
  const { data: subscriptions, isLoading } = useApiGetter(SubscriptionService, 'listSubscriptions', [], {
    defaultValue: [],
    enable: can(Permission.READ_SUBSCRIPTIONS),
  });

  const userHasStudentPlan = subscriptions?.some(
    (sub) => sub.plan.family === PlanFamily.STUDENT && sub.status === SubscriptionStatus.ACTIVE,
  );

  const userHasPaidSubscription = subscriptions?.some(
    (sub) => sub.billing_channel !== BillingChannel.NONE && sub.status === SubscriptionStatus.ACTIVE,
  );

  const showStudentPlanPrompt =
    can(Permission.READ_SUBSCRIPTIONS) &&
    !isLoading &&
    !userHasStudentPlan &&
    !userHasPaidSubscription &&
    !userHasOnlySnowflakeSub;

  return (
    <BasicPageLayout title="Billing" subtitle={subtitle}>
      <div style={{ display: 'flex', gap: '32px', flexDirection: 'column', marginTop: '32px' }}>
        <Stack gap={2}>
          {can(Permission.READ_SUBSCRIPTIONS) && (
            <Box display="flex" flexDirection="column" gap={2}>
              <SectionTitle>Subscriptions</SectionTitle>
              <Subscriptions />
            </Box>
          )}
          {showStudentPlanPrompt && <StudentPlanPrompt />}
        </Stack>
        {can(Permission.UPDATE_ORGANIZATION) && (
          <PageSection title="Billing Details">
            <BillingDetails />
          </PageSection>
        )}
        {can(Permission.READ_CREDIT_CARDS) && (
          <PageSection title="Payment Methods">
            <PaymentMethod />
          </PageSection>
        )}
        {can(Permission.READ_INVOICES) && (
          <PageSection title="Invoices">
            <Invoices />
          </PageSection>
        )}
      </div>
    </BasicPageLayout>
  );
};
